<template>
  <q-page class="q-pa-xl centered-container">
    <div class="fit row wrap justify-between items-start content-start align-start">
      <div class="col">
        <span class="text-h4">{{ i18n.page_title }}</span>
      </div>

      <div class="col-shrink">
        <Link :href="offers_link">
          <q-btn
            class="q-mx-md"
            unelevated
            outline
            :label="$q.screen.gt.sm ? i18n.action_compare_plans : void 0"
            icon="las la-stream"
            no-caps
          />
        </Link>

        <Link :href="offers_link">
          <q-btn
            color="primary"
            icon="las la-share"
            unelevated
            :label="$q.screen.gt.xs ? i18n.action_change_plan : void 0"
            no-caps
          />
        </Link>
      </div>
    </div>

    <!-- Unknown subscription -->
    <div
      v-if="!subscription"
      class="row q-mt-md q-col-gutter-md"
    >
      <div class="col-12">
        <q-banner
          class="bg-transparent "
          inline-actions
        >
          <template v-slot:avatar>
            <q-icon
              name="error"
              color="negative"
            />
          </template>
          {{ i18n.subscription_error }}
        </q-banner>
      </div>
    </div>

    <div
      v-else
      class="row q-mt-md q-col-gutter-lg q-mb-xl"
    >
      <!-- Plan details -->
      <div class="col-12 col-sm-6 col-md-6">
        <q-card class="q-px-md">
          <q-card-section>
            <!-- Title and icon -->
            <q-item class="q-pa-none">
              <q-item-section>
                <q-item-label class="flex items-center">
                  <span class="text-h6 text-bold q-mr-sm">
                    {{ subscription.title }}
                  </span>

                  <q-chip
                    outline
                    color="primary"
                    size="12px"
                  >
                    {{ subscription.billing_interval_label }}
                  </q-chip>
                </q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-chip
                  size="12px"
                  :color="subscription_status_chip_color(subscription.status).color"
                  :text-color="subscription_status_chip_color(subscription.status).text_color"
                >
                  {{ subscription.status_label }}
                </q-chip>
              </q-item-section>
            </q-item>

            <!-- details -->
            <q-item class="q-pa-none">
              <q-item-section>
                <q-item-label class="flex items-center">
                  <span class="text-bold">
                    {{ subscription.price }}

                    <span class="text-secondary-caption">{{ subscription.price_per_interval }}</span>
                  </span>
                </q-item-label>

                <q-item-label class="text-secondary-caption">
                  {{ subscription.status_description }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-card-section>

          <q-separator />

          <q-card-section>
            <template v-if="subscription.stripe_id">
              <Link :href="offers_link">
                <span class="link-style text-primary">{{ i18n.action_change_plan }}</span>
              </Link>
              <span class="text-light q-px-sm"> | </span>
              <span
                class="link-style text-primary"
                @click="subscription_renew_toggle(subscription)"
              >{{ subscription.renewal_action }}</span>
            </template>
            <template v-else>
              <Link :href="offers_link">
                <span class="link-style text-primary">{{ i18n.action_change_plan }}</span>
              </Link>
            </template>
          </q-card-section>

          <q-inner-loading :showing="subscription_renew_toggle_loading">
            <q-spinner-tail
              color="primary"
              size="lg"
            />
          </q-inner-loading>
        </q-card>
      </div>

      <!-- Billing owner -->
      <div class="col-12 col-sm-6 col-md-6">
        <q-card class="q-px-md">
          <q-card-section>
            <!-- Title and icon -->
            <q-item class="q-pa-none">
              <q-item-section>
                <q-item-label class="flex items-center">
                  <span class="text-h6 text-bold q-mr-sm">{{ i18n.subscriber_title }}</span>
                </q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-avatar size="2rem">
                  <img
                    :src="subscriber.avatar_url"
                    referrerpolicy="no-referrer"
                  >
                </q-avatar>
              </q-item-section>
            </q-item>

            <!-- details -->
            <q-item class="q-pa-none">
              <q-item-section>
                <q-item-label class="">
                  <span class="text-bold">
                    {{ subscriber.name }}
                  </span>
                </q-item-label>

                <q-item-label class="text-secondary-caption">
                  {{ subscriber.email }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-card-section>

          <q-separator />

          <q-card-section>
            <span
              class="link-style text-primary"
              @click="subscriber_change_dialog"
            > {{ i18n.action_change_subscriber }} </span>
          </q-card-section>
        </q-card>
      </div>

      <!-- Transactions -->
      <div class="col-12 col-sm-6 col-md-6">
        <q-card class="q-px-md">
          <q-card-section>
            <!-- Title and icon -->
            <q-item class="q-pa-none">
              <q-item-section>
                <q-item-label class="flex items-center">
                  <span class="text-h6 text-bold q-mr-sm">{{ i18n.transactions_title }}</span>
                </q-item-label>
              </q-item-section>
              <q-item-section side>
                <!-- Progress circular -->
                <q-circular-progress
                  :value="workspace.total_transactions_count"
                  :max="subscription.feature_max_transactions"
                  size="2rem"
                  :color="progress_color(
                    workspace.total_transactions_count,
                    subscription.feature_max_transactions
                  )
                  "
                  track-color="grey-3"
                  :thickness="0.2"
                />
              </q-item-section>
            </q-item>

            <!-- details -->
            <q-item class="q-pa-none">
              <q-item-section>
                <q-item-label
                  :class="{
                    'text-negative':
                      workspace.total_transactions_count >
                      subscription.feature_max_transactions,
                  }"
                >
                  <span class="text-bold">
                    {{ workspace.total_transactions_count }}
                  </span>

                  <span class=""> {{ i18n.transactions_used }}</span>
                </q-item-label>

                <q-item-label class="text-secondary-caption">
                  <span v-html="subscription.transactions_limit_description" />
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-card-section>

          <q-separator />

          <q-card-section>
            <Link :href="offers_link">
              <span class="link-style text-primary"> {{ i18n.action_increase_limit }} </span>
            </Link>
          </q-card-section>
        </q-card>
      </div>

      <!-- Sources -->
      <div class="col-12 col-sm-6 col-md-6">
        <q-card class="q-px-md">
          <q-card-section>
            <!-- Title and icon -->
            <q-item class="q-pa-none">
              <q-item-section>
                <q-item-label class="flex items-center">
                  <span class="text-h6 text-bold q-mr-sm">{{ i18n.sources_title }}</span>
                </q-item-label>
              </q-item-section>
              <q-item-section side>
                <!-- Progress circular -->
                <q-circular-progress
                  v-if="subscription.feature_max_exchange_accounts != 999"
                  :value="workspace.exchange_accounts_count"
                  :max="subscription.feature_max_exchange_accounts"
                  size="2rem"
                  :color="progress_color(
                    workspace.exchange_accounts_count,
                    subscription.feature_max_exchange_accounts
                  )
                  "
                  track-color="grey-3"
                  :thickness="0.2"
                />
                <!-- Infinite / Unlimited -->
                <q-icon
                  v-if="subscription.feature_max_exchange_accounts == 999"
                  name="las la-infinity"
                />
              </q-item-section>
            </q-item>

            <!-- details -->
            <q-item class="q-pa-none">
              <q-item-section>
                <q-item-label
                  :class="{
                    'text-negative':
                      workspace.exchange_accounts_count > subscription.feature_max_exchange_accounts && subscription.feature_max_exchange_accounts != 999,
                  }"
                >
                  <span class="text-bold">
                    {{ workspace.exchange_accounts_count }}
                  </span>

                  <span class=""> {{ i18n.sources_used }}</span>
                </q-item-label>

                <q-item-label class="text-secondary-caption">
                  <span v-html="subscription.exchange_accounts_limit_description" />
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-card-section>

          <q-separator />

          <q-card-section>
            <Link :href="offers_link">
              <span class="link-style text-primary"> {{ i18n.action_increase_limit }} </span>
            </Link>
          </q-card-section>
        </q-card>
      </div>

      <!-- Settings -->
      <div class="col-12 col-sm-6 col-md-6">
        <q-card class="q-px-md">
          <q-card-section>
            <!-- Title and icon -->
            <q-item class="q-pa-none">
              <q-item-section>
                <q-item-label class="">
                  <span class="text-h6 text-bold q-mr-sm">{{ i18n.payment_title }}</span>
                </q-item-label>
              </q-item-section>
              <q-item-section side>
                <img
                  src="/image/stripe_logo.svg"
                  alt="Stripe"
                  style="height: 32px; margin-top: 1px"
                >
              </q-item-section>
            </q-item>

            <!-- details -->
            <q-item class="q-pa-none">
              <q-item-section>
                <q-item-label class="text-secondary-caption">
                  {{ i18n.payment_billing_info }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-card-section>

          <q-separator />

          <q-card-section>
            <template v-if="subscription.stripe_id && $page.props.auth.user.id == subscriber?.id">
              <span
                class="link-style text-primary"
                @click="stripe_portal('payment_method')"
              >{{ i18n.payment_method }}</span>
              <span class="text-light q-px-sm"> | </span>
              <span
                class="link-style text-primary"
                @click="stripe_portal('customer')"
              >{{ i18n.payment_address }}</span>
            </template>

            <template v-else>
              <span class="text-light">{{ i18n.payment_no_info }}</span>
            </template>
          </q-card-section>
        </q-card>
      </div>

      <!-- Invoices -->
      <div class="col-12 col-sm-6 col-md-6">
        <q-card class="q-px-md">
          <q-card-section>
            <!-- Title and icon -->
            <q-item class="q-pa-none">
              <q-item-section>
                <q-item-label class="r">
                  <span class="text-h6 text-bold q-mr-sm">{{ i18n.invoices_title }}</span>
                </q-item-label>
              </q-item-section>
              <q-item-section avatar>
                <q-avatar
                  color="pastel-blue"
                  size="32px"
                >
                  <q-icon
                    name="las la-receipt"
                    color="black"
                    size="22px"
                  />
                </q-avatar>
              </q-item-section>
            </q-item>

            <!-- details -->
            <q-item class="q-pa-none">
              <q-item-section>
                <q-item-label class="text-secondary-caption">
                  {{ i18n.action_invoices_access }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-card-section>

          <q-separator />

          <q-card-section>
            <template v-if="subscription.stripe_id">
              <span
                class="link-style text-primary"
                @click="invoices_last_open"
              >{{ i18n.action_invoices_show_last }}</span>
              <span class="text-light q-px-sm"> | </span>
              <span
                class="link-style text-primary"
                @click="invoices_dialog_open"
              >{{ i18n.action_invoices_show }}</span>
            </template>

            <template v-else>
              <span class="text-light">{{ i18n.invoices_empty }}</span>
            </template>
          </q-card-section>
        </q-card>
      </div>

      <!-- Features -->
      <div class="col-12">
        <q-card class="q-px-md">
          <q-card-section>
            <!-- Title and icon -->
            <q-item class="q-pa-none">
              <q-item-section>
                <q-item-label class="flex items-center">
                  <span class="text-h6 text-bold q-mr-sm">{{ i18n.features_title }}</span>
                </q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-icon
                  name="las la-list-alt"
                  size="2rem"
                  color="secondary"
                />
              </q-item-section>
            </q-item>

            <!-- features -->
            <q-list class="q-pa-none">
              <q-item
                v-for="(feature, index) in features"
                :key="`feature_${index}`"
                class="q-my-md q-px-none"
              >
                <q-item-section avatar>
                  <q-icon
                    :color="feature.enabled ? 'positive' : 'negative'"
                    :name="feature.enabled ? 'check_circle' : 'cancel'"
                  />
                </q-item-section>

                <q-item-section>
                  <q-item-label class="text-bold">
                    {{ feature.label }}
                  </q-item-label>
                  <q-item-label
                    caption
                    class="text-secondary-caption"
                  >
                    {{ feature.description }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-card-section>

          <q-separator />

          <q-card-section>
            <Link :href="offers_link">
              <span class="link-style text-primary">{{ i18n.action_unlock_features }}</span>
            </Link>
          </q-card-section>
        </q-card>
      </div>
    </div>

    <!-- Invoices List -->
    <q-dialog v-model="invoices_dialog_show">
      <q-card
        class="q-pa-xl"
        style="width: 700px; max-width: 80vw"
      >
        <div class="row text-center">
          <div class="col-12 text-center">
            <q-avatar
              color="pastel-blue"
              size="56px"
            >
              <q-icon
                name="las la-receipt"
                color="black"
                size="34px"
              />
            </q-avatar>
          </div>
          <div class="col-12 q-pt-lg">
            <span class="text-h6">{{ i18n.invoices_title }}</span>

            <q-card-section class="q-mt-lg">
              <q-markup-table
                v-if="invoices.length"
                flat
              >
                <tbody>
                  <tr
                    v-for="invoice in invoices"
                    :key="invoice.id"
                  >
                    <td class="text-left text-bold">
                      {{ invoice.number }}
                    </td>
                    <td class="text-left">
                      {{ invoice.created }}
                    </td>
                    <td class="text-right">
                      {{ invoice.amount }}
                    </td>
                    <td class="text-center">
                      <q-chip
                        size="12px"
                        :class="invoice_status_class(invoice)"
                      >
                        {{ invoice.status }}
                      </q-chip>
                    </td>
                    <td class="text-right">
                      <a
                        v-if="invoice.file_url"
                        :href="invoice.file_url"
                        download
                      >
                        <q-btn
                          flat
                          icon="las la-download"
                          color="primary"
                          round
                          no-caps
                        />
                      </a>
                      <a
                        v-if="invoice.file_hosted_url"
                        :href="invoice.file_hosted_url"
                        target="_blank"
                      >
                        <q-btn
                          flat
                          icon="link"
                          color="primary"
                          round
                          no-caps
                        />
                      </a>
                    </td>
                  </tr>
                </tbody>
              </q-markup-table>
              <div
                v-if="invoices_dialog_loading == false && invoices.length == 0"
                class="text-center"
              >
                <p class="text-bold q-mt-md">
                  {{ i18n.invoices_empty }}
                </p>
              </div>

              <q-inner-loading :showing="invoices_dialog_loading">
                <q-spinner-tail
                  color="primary"
                  size="lg"
                />
              </q-inner-loading>
            </q-card-section>

            <div class="col-12 q-mt-lg">
              <q-btn
                :label="$q.lang.label.close"
                flat
                class="btn-underline"
                @click="invoices_dialog_show = false"
              />
            </div>
          </div>
        </div>
      </q-card>
    </q-dialog>
  </q-page>
</template>
<script>
import Layout from '@/layouts/workspace.vue';
import { Link } from '@inertiajs/vue3';

export default {
  components: {
    Link,
  },
  layout: Layout,
  props: {
    i18n: {
      type: Object,
      required: true,
    },
    workspace: {
      type: Object,
      required: true,
    },
    offers_link: {
      type: String,
      required: true,
    },
    subscription_renew_action: {
      type: String,
      required: true,
    },
    subscription: {
      type: Object,
      required: false,
    },
    subscriber: {
      type: Object,
      required: false,
    },
    features: {
      type: Array,
      required: false,
    },
    invoices: {
      type: Array,
      default: () => { return [] },
      required: false,
    },
    stripe_portal_action: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      invoices_dialog_show: false,
      invoices_dialog_loading: false,
      subscription_renew_toggle_loading: false,
    };
  },
  computed: {
  },
  watch: {
  },
  methods: {
    subscription_renew_toggle: function (sub) {
      let end_of_period = "cancel";
      if (sub.renewal) {
        end_of_period = "cancel";
      } else {
        end_of_period = "renew";
      }

      this.$inertia.post(
        this.subscription_renew_action,
        { end_of_period: end_of_period },
        {
          only: ['subscription', 'flash'],
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.subscription_renew_toggle_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.subscription_renew_toggle_loading = false;
          },
        }
      );
    },
    invoices_last_open: function () {
      this.$inertia.get(
        '',
        {},
        {
          only: ['invoices'],
          preserveState: true,
          preserveScroll: true,
          onSuccess: () => {
            if (this.invoices.length > 0) {
              window.open(this.invoices[0].file_hosted_url, '_blank');
            }
          }
        }
      );
    },
    invoices_dialog_open: function () {
      this.$inertia.get(
        '',
        {},
        {
          only: ['invoices'],
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.invoices_dialog_loading = true;
            this.invoices_dialog_show = true;
          },
          onSuccess: () => {
          },
          onError: (errors) => {
            console.log(errors);
          },
          onFinish: () => {
            this.invoices_dialog_loading = false;
          },
        }
      );
    },
    stripe_portal: function (page) {
      this.$rails.post(
        this.stripe_portal_action,
        { page: page }
      ).then((response) => {
        if (!response.ok) {
          throw new Error(this.i18n.error);
        }
        return response.json();
      }).then((json) => {
        window.location.href = json.url;
      }).catch((error) => {
        console.log(error);
        this.$q.notify({
          message: error,
          multiline: true,
          type: "negative",
        });
      });
    },
    subscription_status_chip_color: function (status) {
      switch (status) {
      case 'active':
        return { color: "pastel-green", text_color: "black" };
      case 'paused':
        return { color: "pastel-orange", text_color: "black" };
      case 'canceled':
        return { color: "pastel-red", text_color: "black" };
      case 'unpaid':
        return { color: "black", text_color: "white" };
      case 'past_due':
        return { color: "red", text_color: "white" };
      case 'incomplete':
        return { color: "accent", text_color: "white" };
      case 'incomplete_expired':
        return { color: "secondary", text_color: "white" };
      case 'trialing':
        return { color: "pastel-blue", text_color: "black" };
      default:
        return { color: "black", text_color: "white" };
      }
    },
    progress_color(value, max) {
      if (value < max * 0.5) return "accent";
      if (value < max * 0.75) return "warning";
      return "negative";
    },
    subscriber_change_dialog: function () {
      this.$q.dialog({
        title: this.i18n.subscriber_change_title,
        message: this.i18n.subscriber_change_message,
        ok: this.$q.lang.label.close,
      });
    },
    invoice_status_class: function (invoice) {
      switch (invoice.status) {
      case "draft":
        return "invoice-status-draft";
      case "deleted":
        return "invoice-status-deleted";
      case "open":
        return "invoice-status-open";
      case "paid":
        return "invoice-status-paid";
      case "uncollectible":
        return "invoice-status-uncollectible";
      case "void":
        return "invoice-status-void";
      default:
        return "";
      }
    },
  },
};
</script>
